"use client"
import { AlertCircle, CheckCircle, File, Image as ImageIcon, Plus, Upload, Video, X } from 'lucide-react';
import Image from 'next/image';
import React, { useCallback, useState } from 'react';

interface UploadedFile {
  id: string;
  file: File;
  preview: string;
  type: 'image' | 'video' | 'other';
  name: string;
  size: string;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  errorMessage?: string;
  uploadedUrl?: string;
}

interface MediaUploadProps {
  onUploadComplete?: () => void;
  defaultShowUploadArea?: boolean;
}

export default function MediaUpload({ onUploadComplete, defaultShowUploadArea = false }: MediaUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [showUploadArea, setShowUploadArea] = useState(defaultShowUploadArea);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileType = (file: File): 'image' | 'video' | 'other' => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    return 'other';
  };

  const createPreview = (file: File): string => {
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file);
    }
    return '';
  };

  const uploadFile = useCallback(async (fileData: UploadedFile) => {
    try {
      const formData = new FormData();
      formData.append('file', fileData.file);

      const response = await fetch('/api/admin/media/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload file');
      }

      const result = await response.json();

      // Update file status to success
      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === fileData.id
            ? { ...f, status: 'success', progress: 100, uploadedUrl: result.fileUrl }
            : f
        )
      );

      // Notify parent component
      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      // Update file status to error
      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === fileData.id
            ? {
              ...f,
              status: 'error',
              errorMessage: error instanceof Error ? error.message : 'Upload failed'
            }
            : f
        )
      );
    }
  }, [onUploadComplete]);

  const handleFiles = useCallback((files: FileList) => {
    const newFiles: UploadedFile[] = Array.from(files).map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      preview: createPreview(file),
      type: getFileType(file),
      name: file.name,
      size: formatFileSize(file.size),
      progress: 0,
      status: 'uploading'
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);

    // Upload each file
    newFiles.forEach(fileData => {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadedFiles(prev =>
          prev.map(f =>
            f.id === fileData.id && f.progress < 90
              ? { ...f, progress: Math.min(f.progress + 10, 90) }
              : f
          )
        );
      }, 200);

      // Actually upload the file
      uploadFile(fileData).finally(() => {
        clearInterval(progressInterval);
      });
    });
  }, [uploadFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    handleFiles(files);
  }, [handleFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      handleFiles(files);
    }
    // Reset input
    e.target.value = '';
  }, [handleFiles]);

  const removeFile = (id: string) => {
    setUploadedFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== id);
    });
  };

  const clearCompleted = () => {
    setUploadedFiles(prev => {
      prev.forEach(f => {
        if (f.preview && f.status === 'success') {
          URL.revokeObjectURL(f.preview);
        }
      });
      return prev.filter(f => f.status !== 'success');
    });
  };

  const retryFailed = (fileData: UploadedFile) => {
    setUploadedFiles(prev =>
      prev.map(f => {
        if (f.id === fileData.id) {
          const { errorMessage: _, ...rest } = f;
          return { ...rest, status: 'uploading' as const, progress: 0 };
        }
        return f;
      })
    );
    uploadFile(fileData);
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <ImageIcon className="h-8 w-8 text-blue-500" />;
      case 'video':
        return <Video className="h-8 w-8 text-purple-500" />;
      default:
        return <File className="h-8 w-8 text-gray-500" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'uploading':
        return 'text-yellow-600';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const completedCount = uploadedFiles.filter(f => f.status === 'success').length;
  const failedCount = uploadedFiles.filter(f => f.status === 'error').length;

  return (
    <div className="bg-white rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          {uploadedFiles.length > 0 && (
            <p className="text-sm text-gray-500">
              {completedCount} completed, {failedCount} failed
            </p>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {uploadedFiles.some(f => f.status === 'success') && (
            <button
              onClick={clearCompleted}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Clear completed
            </button>
          )}
          <button
            onClick={() => setShowUploadArea(!showUploadArea)}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <Plus className="h-4 w-4" />
            <span>{showUploadArea ? 'Hide' : 'Show'} Upload Area</span>
          </button>
        </div>
      </div>

      {showUploadArea && (
        <div className="space-y-4">
          {/* Drag & Drop Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragOver
              ? 'border-primary bg-primary/5'
              : 'border-gray-300 hover:border-gray-400'
              }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <div className="space-y-2">
              <p className="text-lg font-medium text-gray-900">
                Drop files here or click to browse
              </p>
              <p className="text-sm text-gray-500">
                Support for images (JPG, PNG, GIF, WebP, SVG) and videos (MP4, WebM, OGG, MOV, AVI)
              </p>
              <p className="text-xs text-gray-400">
                Maximum file size: 50MB per file
              </p>
            </div>
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleFileInput}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
            >
              Choose Files
            </label>
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-3 mt-4">
          <h4 className="text-sm font-medium text-gray-700">Upload Progress</h4>
          {uploadedFiles.map(file => (
            <div key={file.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0">
                {file.preview ? (
                  <Image
                    src={file.preview}
                    alt={file.name}
                    width={48}
                    height={48}
                    className="h-12 w-12 object-cover rounded"
                  />
                ) : (
                  getFileIcon(file.type)
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file.name}
                  </p>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(file.status)}
                    <button
                      onClick={() => removeFile(file.id)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center space-x-3 mt-1">
                  <span className="text-xs text-gray-500">{file.size}</span>
                  <span className={`text-xs font-medium ${getStatusColor(file.status)}`}>
                    {file.status === 'uploading' ? `Uploading... ${file.progress}%` :
                      file.status === 'success' ? 'Uploaded' : 'Error'}
                  </span>
                </div>

                {file.status === 'uploading' && (
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {file.status === 'error' && file.errorMessage && (
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-xs text-red-600">{file.errorMessage}</p>
                    <button
                      onClick={() => retryFailed(file)}
                      className="text-xs text-primary hover:text-primary/80"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {file.status === 'success' && file.uploadedUrl && (
                  <p className="text-xs text-gray-500 mt-1 truncate">
                    {file.uploadedUrl}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
